const ChartGenerator = ({ filteredVerbatims, labelTypes, asPercentages, asMixedSentiment }) => {
  const chartData = {};
  const sampleSizes = {}; // To store the number of conversations per label type

  // Define sentiment state positions for easy indexing
  let sentimentStates = asMixedSentiment
    ? ['positive', 'negative/mixed', 'neutral']
    : ['positive', 'negative', 'mixed', 'neutral'];

  // Ensure filteredVerbatims is an array before proceeding
  if (!Array.isArray(filteredVerbatims) || !filteredVerbatims.length) {
    return { chartData, sampleSizes }; // Return empty if filteredVerbatims is not an array or empty
  }

  // Ensure labelTypes is an object before proceeding
  if (!labelTypes || typeof labelTypes !== 'object') {
    return { chartData, sampleSizes }; // Return empty if labelTypes is not an object
  }

  // Iterate over each label type (Gender, Age, etc.)
  Object.keys(labelTypes).forEach((labelType) => {
    const counts = {};
    let sampleCount = 0;

    // Count occurrences of each label within the labelType and track sample count
    filteredVerbatims.forEach((verbatim) => {
      // Ensure verbatim and verbatim.labels exist before processing
      const labels = verbatim?.labels?.[labelType];

      if (Array.isArray(labels) && labels.length > 0 && labels.every((item) => typeof item === 'object' && item !== null)) {
        sampleCount++; // If verbatim has any label of this type, increase sample count

        // Check if the label type is sentiment-enabled but not "Sentiment" itself
        if (labelTypes?.[labelType]?.sentimentEnabled && labelType !== "Sentiment") {
          // For sentiment-enabled labels, create an array to hold counts for each state
          labels.forEach((label) => {
            if (label && typeof label === 'object' && label.name) {
              // Safely destructure after validation
              const { name, state } = label;
              let sentimentState = state || 'neutral';
              // Merge negative and mixed states if asMixedSentiment is true
              if (asMixedSentiment && (sentimentState === 'negative' || sentimentState === 'mixed')) sentimentState = 'negative/mixed';
              const stateIndex = sentimentStates.indexOf(sentimentState);
              // Initialize counts for each sentiment state if not already done
              if (!counts[name]) counts[name] = new Array(sentimentStates.length).fill(0);

              counts[name][stateIndex] += 1;
            } else {
              console.warn('Invalid label encountered:', label);
            }
          });
        } else {
          // Normal counting for non-sentiment or non-sentiment-enabled label types
          labels.forEach((label) => {
            // Ensure label exists and has a name property
            if (label?.name) {
              // Non-sentiment label just has one count value
              if (!counts[label.name]) counts[label.name] = [0];
              // Increment the single count for non-sentiment labels
              counts[label.name][0] += 1;
            }
          });
        }
      }
    });

    // Merge negative and mixed counts if asMixedSentiment is true for sentiment graph
    if (asMixedSentiment && labelType === "Sentiment") {
      counts["negative/mixed"] = [counts["mixed"][0] + counts["negative"][0]];
      delete counts["mixed"];
      delete counts["negative"];
    }

    // If asPercentages is true, convert raw counts to percentages
    if (asPercentages && sampleCount > 0) {
      Object.keys(counts).forEach((key) => {
        // Convert each count to a percentage
        counts[key] = counts[key].map((count) => (count / sampleCount) * 100);
      });
    }

    // Store the data for this label type
    chartData[labelType] = counts;
    // Store sample count for this label type
    sampleSizes[labelType] = sampleCount;
  });
  // Return both chart data and sample sizes
  return { chartData, sampleSizes };
};

export default ChartGenerator;
